import React, { useCallback, useRef, useState } from 'react';

// Import types
import type { HoverInfo } from '../../types/tooltip';

const DEFAULT_DELAY_MS = 1500;

interface UseHoverTooltipProps {
  delayMs?: number;
  disabled?: boolean;
}

/**
 * useHoverTooltip
 *
 * Manages delayed hover tooltips for grid cells. Provides handlers compatible
 * with React mouse events. Tooltip will not show when disabled.
 */
const useHoverTooltip = ({
  delayMs = DEFAULT_DELAY_MS,
  disabled = false,
}: UseHoverTooltipProps) => {
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const clear = useCallback(() => {
    clearTimer();
    setHoverInfo(null);
  }, []);

  const startHover = useCallback(
    (dateKey: string, row: number, e: React.MouseEvent) => {
      if (disabled) return;
      clearTimer();
      const { clientX, clientY } = e;
      timerRef.current = window.setTimeout(() => {
        setHoverInfo({ dateKey, row, x: clientX, y: clientY });
      }, delayMs);
    },
    [delayMs, disabled],
  );

  const stopHover = useCallback(() => {
    if (disabled) return;
    clear();
  }, [clear, disabled]);

  const moveHover = useCallback((_e: React.MouseEvent) => {
    // Tooltip position is fixed where it first appeared to reduce jitter.
    // Intentionally no-op.
  }, []);

  return { hoverInfo, startHover, stopHover, moveHover, clear };
};

export default useHoverTooltip;
